<template>
  <div style="width: 100%">
    <v-row>
      <v-col cols="12">
        <v-row>
          <select-multiple-unidades
            v-model="unidades"
            :mapeamento-id="diagnostico.mapeamento.id"
          />
        </v-row>

        <div v-if="unidades.selecionado.length">
          <!-- há desempenhos -->
          <div v-if="desempenhosUnidadesViabilizados.length">
            <!-- pies distribuição de cds por categoria -->
            <v-row>
              <v-col>
                <pie-chart-categoria-c-d-s
                  :desempenhos="desempenhosUnidadesViabilizados"
                  :fonte="fontePieChart"
                  :titulo="tituloPieChart"
                  com-sub-piechart
                  :is-get-svg="isGetSvg"
                  @svg="(value) => svg = value"
                />
              </v-col>
            </v-row>

            <!-- msg caso haja unidades sem resultado viabilizado -->
            <v-row
              v-if="unidadesInviabilizadas.length > 0"
              class="justify-center"
            >
              <p class="caption text-center">
                {{ msgUnidadesInviabilizadas }}
              </p>
            </v-row>

            <!-- legenda do pie-chart -->
            <v-row class="justify-center">
              <legenda-cds />
            </v-row>

            <!-- tabela de resultados -->
            <v-row>
              <v-col>
                <tabela-resultados-c-d-s
                  :dados="dadosTabelaCds"
                  :is-uma-unidade="false"
                  :titulo="tituloTabelaResultados"
                />
              </v-col>
            </v-row>

            <!-- unidades inviabilizadas -->
            <v-row v-if="unidadesInviabilizadas.length > 0">
              <v-col>
                <v-data-table
                  :headers="headerTabelaInviabilizados"
                  :items="unidadesInviabilizadas"
                >
                  <template v-slot:top>
                    <v-row class="mb-2">
                      <v-toolbar flat>
                        <v-toolbar-title>
                          <h2
                            class="
                              title
                              primary--text
                              text--darken-1 text-no-wrap
                            "
                          >
                            {{ tituloTabelaInviabilizadas }}
                          </h2>
                        </v-toolbar-title>
                      </v-toolbar>
                    </v-row>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row class="justify-end mb-1">
              <v-tooltip
                max-width="400"
                top
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    class="mr-2"
                    color="success"
                    @click="getRelatorio()"
                    v-on="on"
                  >
                    <v-icon>get_app</v-icon>
                    BAIXAR PDF
                  </v-btn>
                </template>
                <span>Clique para baixar o relatório dos dados em formato PDF.</span>
              </v-tooltip>
            </v-row>
          </div>

          <!-- não há desempenhos -->
          <v-row v-else>
            <v-col>
              <app-alert
                icon="info"
                :message="resultadoInviabilizado"
                type="info"
              />
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import AppAlert from '@/components/core/AppAlert';
import SelectMultipleUnidades from '@/components/SelectMultipleUnidades';
import PieChartCategoriaCDS from '@/components/monitoramento/desempenho/cds/PieChartCategoriaCDS';
import LegendaCds from '@/components/monitoramento/desempenho/cds/LegendaCds';
import TabelaResultadosCDS from '@/components/monitoramento/desempenho/cds/TabelaResultadosCDS';
import { sum, mean, std } from 'mathjs';
import { generatePDF, preparePdf, convertSvg } from '@/modules/relatorioPDF';
import Data from '@/modules/data';
import toDecimal from '@/filters/decimal';

export default {
  name: 'RelatorioGerencialCoeficienteDesempenho',

  components: {
    AppAlert,
    SelectMultipleUnidades,
    PieChartCategoriaCDS,
    LegendaCds,
    TabelaResultadosCDS,
  },

  props: {
    diagnostico: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      unidades: {
        isTodasSelecionadas: null,
        selecionado: [],
        visualizacao: '',
      },
      headerTabelaInviabilizados: [
        {
          text: 'NOME DA UNIDADE',
          value: 'nome',
          width: '60%',
          sortable: false,
        },
        {
          text: 'SIGLA DA UNIDADE',
          value: 'sigla',
          width: '40%',
          sortable: false,
          align: 'center',
        },
      ],
      resultadoInviabilizado:
        'Resultado inviabilizado (Ausência do resultado comportamental) (Ausência do resultado técnico).',
      isGetSvg: false,
      svg: null,
    };
  },

  computed: {
    ...mapState({
      desempenhos: (state) => state.monitoramentoDesempenho.desempenhos,
    }),
    tituloPieChart() {
      return `Comparativo de CDS ${this.unidades.visualizacao} ${this.unidadesInviabilizadas.length ? '*' : ''}`;
    },
    fontePieChart() {
      return `Fonte: Sistema Desenvolva, ${this.diagnostico.ano}`;
    },
    desempenhosUnidades() {
      return this.desempenhos.filter((d) => this.unidades.selecionado.some((u) => u.idVersao === d.unidade.id));
    },
    desempenhosUnidadesViabilizados() {
      return this.desempenhosUnidades.filter((d) => this.isCdsViabilizado(d));
    },
    unidadesInviabilizadas() {
      return this.unidades.selecionado.filter(
        (u) => !this.desempenhos.some((d) => d.unidade.id === u.idVersao && this.isCdsViabilizado(d)),
      );
    },
    unidadesViabilizadas() {
      return this.unidades.selecionado
        .filter((u) => this.desempenhos.some((d) => d.unidade.id === u.idVersao && this.isCdsViabilizado(d)));
    },
    tituloTabelaInviabilizadas() {
      return `CDS INVIABILIZADOS (${this.unidadesInviabilizadas.map((u) => u.sigla).join(', ')})`;
    },
    tituloTabelaResultados() {
      if (this.unidadesViabilizadas.length === this.unidades.selecionado.length) {
        return 'RESULTADOS (TODAS)';
      }

      return `RESULTADOS (${this.unidadesViabilizadas.map((u) => u.sigla).join(', ')})`;
    },
    msgUnidadesInviabilizadas() {
      if (this.unidadesInviabilizadas.length > 1) {
        return `* As unidades (${this.unidadesInviabilizadas
          .map((u) => u.sigla)
          .join(', ')}) não possuem resultado viabilizado.`;
      }
      if (this.unidadesInviabilizadas.length === 1) {
        return `* A unidade ${this.unidadesInviabilizadas[0].sigla} não possui resultado viabilizado.`;
      }
      return '';
    },
    desempenhosPorUnidade() {
      return _.groupBy(this.desempenhosUnidadesViabilizados, 'unidade.id');
    },
    dadosTabelaCds() {
      const dados = this.unidadesViabilizadas.map((u) => {
        const desempenhos = this.desempenhosPorUnidade[u.idVersao];
        return {
          unidade: u,
          nServidoresNaoAtende: desempenhos.filter((d) => d.cdsCategoria === 'NAO_ATENDE').length,
          nServidoresEmDesenvolvimento: desempenhos.filter((d) => d.cdsCategoria === 'EM_DESENVOLVIMENTO').length,
          nServidoresAtende: desempenhos.filter((d) => d.cdsCategoria === 'ATENDE').length,
          mediaCds: desempenhos.length > 0 ? mean(desempenhos.map((d) => d.cds)) : 0,
          desvioPadraoCds: desempenhos.length > 0 ? std(desempenhos.map((d) => d.cds)) : 0,
        };
      });

      return {
        linhas: dados,
        total: {
          nServidoresNaoAtende: sum(dados.map((d) => d.nServidoresNaoAtende)),
          nServidoresEmDesenvolvimento: sum(dados.map((d) => d.nServidoresEmDesenvolvimento)),
          nServidoresAtende: sum(dados.map((d) => d.nServidoresAtende)),
          mediaCds: mean(this.desempenhosUnidadesViabilizados.map((d) => d.cds)),
          desvioPadraoCds: std(this.desempenhosUnidadesViabilizados.map((d) => d.cds)),
        },
      };
    },
  },

  methods: {
    isCdsViabilizado(d) {
      return (
        !d.foiDispensado
        && d.teveAutoavaliacaoComportamental
        && d.teveAvaliacaoGestorComportamental
        && d.teveAutoavaliacaoResponsabilidade
        && d.teveAvaliacaoGestorResponsabilidade
      );
    },
    async getRelatorio() {
      this.isGetSvg = true;
      await preparePdf(async () => {
        const inicioComportamental = Data.format(this.diagnostico.inicioComportamental);
        const fimComportamental = Data.format(this.diagnostico.fimComportamental);
        const inicioResponsabilidade = Data.format(this.diagnostico.inicioResponsabilidadeChefia);
        const fimResponsabilidade = Data.format(this.diagnostico.fimResponsabilidadeServidor);

        const documento = {
          nome: `relatorio-monitoramento-coeficiente-desempenho-geral-${this.diagnostico.id}`,
          orientacao: 'landscape',
          cabecalho: {
            titulo: 'Coeficiente de Desempenho Geral',
            itens: [
              `Diagnóstico: ${this.diagnostico.nome}`,
              `Período de avaliação comportamental: ${inicioComportamental} a ${fimComportamental}`,
              `Período de avaliação de responsabilidades: ${inicioResponsabilidade} a ${fimResponsabilidade}`,
              `Unidades Viabilizadas: ${this.unidadesViabilizadas.map((u) => u.sigla).join(',')}`,
              `Unidade Inviabilizadas: ${this.unidadesInviabilizadas.map((u) => u.sigla).join(',')}`,
            ],
          },
          blocos: [
            {
              titulo: 'GRÁFICOS',
              itens: [
                { tipo: 'imagem', valor: await convertSvg(this.svg) },
              ],
            },
            {
              titulo: 'RESULTADOS DAS UNIDADES VIABILIZADAS',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'UNIDADE',
                    'SERVIDORES\nCOM CDS\nNÃO ATENDE',
                    'SERVIDORES\nCOM CDS EM\nDESENVOLVI-\nMENTO',
                    'SERVIDORES\nCOM CDS \nATENDE',
                    'MÉDIA CDS\nDA UNIDADE',
                    'DESVIO PADRÃO\nDA UNIDADE',
                  ],
                  linhas: this.dadosTabelaCds.linhas.map((d) => [
                    `${d.unidade.nome} (${d.unidade.sigla})`,
                    d.nServidoresNaoAtende,
                    d.nServidoresEmDesenvolvimento,
                    d.nServidoresAtende,
                    toDecimal(d.mediaCds),
                    toDecimal(d.desvioPadraoCds),
                  ]),
                  rodape: [
                    'TOTAL',
                    this.dadosTabelaCds.total.nServidoresNaoAtende,
                    this.dadosTabelaCds.total.nServidoresEmDesenvolvimento,
                    this.dadosTabelaCds.total.nServidoresAtende,
                    toDecimal(this.dadosTabelaCds.total.mediaCds),
                    toDecimal(this.dadosTabelaCds.total.desvioPadraoCds),
                  ],
                },
              }],
            },
          ],
        };
        generatePDF(documento);
      });
      this.isGetSvg = false;
    },
    getTotaisCds(desempenhos, unidadeId, tipo) {
      return desempenhos.filter((d) => d.unidade.id = unidadeId && d.cdsCategoria === tipo).length;
    },
    handleTeste(v) {
      window.console.log(v);
    },
  },
};
</script>

<style scoped>
.chart {
  width: 100%;
  max-height: 400px;
  font-family: Roboto, sans-serif;
}
</style>
